<template>
  <article class="invoice-create-card invoice-create-card--accent-billing simple-note-detail-operation-card">
    <header class="invoice-create-card__head">
      <h3>
        <i class="fa fa-briefcase invoice-create-section-icon invoice-create-section-icon--billing" aria-hidden="true"></i>
        {{ operationTitle }}
      </h3>
    </header>

    <div class="simple-note-detail-operation-split">
      <section class="simple-note-detail-operation-pane">
        <dl class="simple-note-detail-operation-list">
          <div class="simple-note-detail-operation-row">
            <dt>{{ fields.status }}</dt>
            <dd>
              <span class="app-status-pill expenses-status-pill" :class="resolvedStatusClass">
                {{ resolvedStatusLabel }}
              </span>
            </dd>
          </div>

          <div class="simple-note-detail-operation-row simple-note-detail-operation-row--customer">
            <dt>{{ fields.customer }}</dt>
            <dd>
              <router-link
                v-if="customerRoute"
                :to="customerRoute"
                class="simple-note-detail-operation-link simple-note-detail-operation-link--customer"
                :title="customerLinkTitle"
              >
                {{ resolvedCustomerName }}
              </router-link>
              <span v-else>{{ resolvedCustomerName }}</span>
            </dd>
          </div>

          <div class="simple-note-detail-operation-row simple-note-detail-operation-row--sale">
            <dt>{{ fields.sale }}</dt>
            <dd>
              <router-link
                v-if="saleRoute"
                :to="saleRoute"
                class="simple-note-detail-operation-link simple-note-detail-operation-link--sale"
                :title="saleLinkTitle"
              >
                {{ resolvedSaleName }}
              </router-link>
              <span v-else>{{ resolvedSaleName }}</span>
            </dd>
          </div>

          <div class="simple-note-detail-operation-spacer" aria-hidden="true"></div>

          <div class="simple-note-detail-operation-row">
            <dt>{{ fields.balance }}</dt>
            <dd>{{ resolvedBalance }}</dd>
          </div>

          <div class="simple-note-detail-operation-row">
            <dt>{{ fields.sent }}</dt>
            <dd>{{ resolvedSent }}</dd>
          </div>

          <div class="simple-note-detail-operation-row">
            <dt>{{ fields.lastSent }}</dt>
            <dd>{{ resolvedLastSent }}</dd>
          </div>
        </dl>
      </section>

      <section class="simple-note-detail-operation-pane simple-note-detail-operation-pane--totals">
        <dl class="simple-note-detail-operation-meta-list">
          <div class="simple-note-detail-operation-meta-row">
            <dt>{{ fields.relatedPayments }}</dt>
            <dd>
              <router-link
                v-if="paymentReceiptRoute"
                :to="paymentReceiptRoute"
                class="simple-note-detail-operation-link"
              >
                {{ resolvedPaymentDisplay }}
              </router-link>
              <span v-else>{{ resolvedPaymentDisplay }}</span>
            </dd>
          </div>

          <div class="simple-note-detail-operation-meta-divider" aria-hidden="true"></div>

          <div class="simple-note-detail-operation-meta-row simple-note-detail-operation-meta-row--comments">
            <dt>{{ fields.comments }}</dt>
            <dd>
              <p
                class="simple-note-detail-operation-comment-text"
                :class="{ 'simple-note-detail-operation-comment-text--expanded': commentExpanded }"
              >
                {{ resolvedCommentsDisplay }}
              </p>

              <button
                v-if="showCommentToggle"
                type="button"
                class="simple-note-detail-operation-comment-toggle"
                @click="commentExpanded = !commentExpanded"
              >
                {{ commentExpanded ? commentToggleLessLabel : commentToggleMoreLabel }}
              </button>
            </dd>
          </div>

          <div class="simple-note-detail-operation-meta-divider" aria-hidden="true"></div>
        </dl>

        <dl class="simple-note-detail-operation-totals-list">
          <div class="simple-note-detail-operation-totals-row simple-note-detail-operation-totals-row--total">
            <dt>{{ totalLabel }}</dt>
            <dd>{{ resolvedTotalAmount }}</dd>
          </div>
        </dl>
      </section>
    </div>
  </article>
</template>

<script>
import { formatDateTime } from '@/utils/datePresentation';
import { formatAmountWithCurrency } from '@/utils/numberFormat';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('simpleNotes');

export default {
  name: 'SimpleNoteDetailOperationCard',
  props: {
    operation: {
      type: Object,
      default: () => ({}),
    },
    comments: {
      type: String,
      default: '',
    },
    total: {
      type: Number,
      default: 0,
    },
    currency: {
      type: String,
      default: 'MXN',
    },
  },
  data() {
    return {
      text,
      commentExpanded: false,
    };
  },
  computed: {
    fallbackValue() {
      return String(this.text?.detail?.page?.dateFallback || '-').trim() || '-';
    },
    operationTitle() {
      return String(this.text?.detail?.cards?.operation?.title || 'Operación').trim() || 'Operación';
    },
    fields() {
      const operationFields = this.text?.detail?.cards?.operation?.fields || {};
      return {
        status: String(operationFields.status || 'Estado').trim() || 'Estado',
        relatedPayments: String(operationFields.relatedPayments || 'Pago').trim() || 'Pago',
        balance: String(operationFields.pendingBalance || operationFields.balance || 'Saldo').trim() || 'Saldo',
        customer: String(operationFields.customerShort || operationFields.customer || 'Cliente').trim() || 'Cliente',
        sale: String(operationFields.sale || 'Negocio').trim() || 'Negocio',
        sent: String(operationFields.sent || 'Enviada').trim() || 'Enviada',
        lastSent: String(operationFields.lastSent || 'Último envío').trim() || 'Último envío',
        comments: String(operationFields.comments || 'Comentarios').trim() || 'Comentarios',
      };
    },
    totalLabel() {
      return String(this.text?.detail?.cards?.totals?.fields?.total || 'TOTAL').trim() || 'TOTAL';
    },
    resolvedStatusLabel() {
      const pendingLabel = String(this.text?.table?.values?.pending || '').trim();
      return String(this.operation?.statusLabel || '').trim() || pendingLabel || this.fallbackValue;
    },
    resolvedStatusClass() {
      const key = String(this.operation?.statusKey || '').trim().toLowerCase();
      return {
        'simple-note-status-pill--paid': key === 'done' || key === 'paid',
        'simple-note-status-pill--partial': key === 'partial',
        'simple-note-status-pill--pending': key === 'pending',
        'simple-note-status-pill--canceled': key === 'canceled',
      };
    },
    resolvedPaymentDisplay() {
      const code = String(this.operation?.paymentReceiptCode || '').trim();
      const codesText = String(this.operation?.paymentReceiptCodesText || '').trim();
      if (codesText !== '') {
        return codesText;
      }

      if (code !== '') {
        return code;
      }

      return '';
    },
    resolvedBalance() {
      const value = Number(this.operation?.balance);
      if (Number.isFinite(value)) {
        return formatAmountWithCurrency(value, this.operation?.currency || 'MXN');
      }

      return this.fallbackValue;
    },
    resolvedCustomerName() {
      const customerName = String(this.operation?.customerName || '').trim();
      if (customerName !== '') {
        return customerName;
      }

      return String(this.text?.detail?.cards?.operation?.values?.noCustomer || 'Sin cliente relacionado').trim()
        || this.fallbackValue;
    },
    customerRoute() {
      const customerId = Number(this.operation?.customerId || 0) || 0;
      if (customerId <= 0) {
        return null;
      }

      return {
        name: 'customerDetail',
        params: { customerId: String(customerId) },
      };
    },
    resolvedSaleName() {
      const saleCode = String(this.operation?.saleCode || '').trim();
      const saleName = String(this.operation?.saleName || '').trim();

      if (saleCode !== '' && saleName !== '') {
        return `${saleCode} - ${saleName}`;
      }

      if (saleCode !== '') {
        return saleCode;
      }

      if (saleName !== '') {
        return saleName;
      }

      return String(this.text?.detail?.cards?.operation?.values?.noSale || 'Sin negocio relacionado').trim()
        || this.fallbackValue;
    },
    saleRoute() {
      const saleId = Number(this.operation?.saleId || 0) || 0;
      if (saleId <= 0) {
        return null;
      }

      return {
        name: 'saleDetail',
        params: { saleId: String(saleId) },
      };
    },
    paymentReceiptRoute() {
      const paymentReceiptId = Number(this.operation?.paymentReceiptId || 0) || 0;
      if (paymentReceiptId <= 0) {
        return null;
      }

      return {
        name: 'paymentReceiptDetail',
        params: { paymentReceiptId: String(paymentReceiptId) },
      };
    },
    resolvedSent() {
      return this.operation?.isSent === true
        ? String(this.text?.detail?.cards?.operation?.values?.sent || 'Enviada').trim() || this.fallbackValue
        : String(this.text?.detail?.cards?.operation?.values?.notSent || 'Sin enviar').trim() || this.fallbackValue;
    },
    resolvedLastSent() {
      return formatDateTime(
        this.operation?.lastSentAt || this.operation?.lastSendAt,
        { fallback: String(this.text?.detail?.cards?.operation?.values?.noLastSent || 'Sin registro').trim() || this.fallbackValue }
      );
    },
    resolvedTotalAmount() {
      return formatAmountWithCurrency(this.total, this.currency);
    },
    resolvedCommentsDisplay() {
      const normalized = String(this.comments || '').trim();
      return normalized;
    },
    showCommentToggle() {
      const normalized = String(this.comments || '').trim();
      return normalized.length > 90;
    },
    commentToggleMoreLabel() {
      return String(this.text?.detail?.cards?.operation?.actions?.showMore || 'Ver más').trim() || 'Ver más';
    },
    commentToggleLessLabel() {
      return String(this.text?.detail?.cards?.operation?.actions?.showLess || 'Ver menos').trim() || 'Ver menos';
    },
    customerLinkTitle() {
      return String(this.text?.table?.links?.customerDetail || 'Abrir detalle del cliente').trim() || 'Abrir detalle del cliente';
    },
    saleLinkTitle() {
      return String(this.text?.table?.links?.saleDetail || 'Abrir detalle del negocio').trim() || 'Abrir detalle del negocio';
    },
  },
};
</script>

<style scoped>
.simple-note-detail-operation-card {
  height: 100%;
  padding: 14px 16px;
}

.simple-note-detail-operation-split {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.95fr);
  gap: 16px;
  align-items: start;
}

.simple-note-detail-operation-pane--totals {
  border-left: 1px solid rgba(148, 163, 184, 0.35);
  padding-left: 16px;
}

.simple-note-detail-operation-list,
.simple-note-detail-operation-meta-list,
.simple-note-detail-operation-totals-list {
  margin: 0;
  padding: 0;
}

.simple-note-detail-operation-row {
  display: grid;
  grid-template-columns: minmax(118px, auto) minmax(0, 1fr);
  align-items: baseline;
  column-gap: 12px;
  margin-bottom: 8px;
}

.simple-note-detail-operation-row:last-child {
  margin-bottom: 0;
}

.simple-note-detail-operation-row dt,
.simple-note-detail-operation-meta-row dt {
  margin: 0;
  font-size: 11px;
  line-height: 1.35;
  font-weight: 600;
  color: #7b879a;
}

.simple-note-detail-operation-row dd,
.simple-note-detail-operation-meta-row dd {
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 600;
  color: #334155;
  word-break: break-word;
}

.simple-note-detail-operation-row--customer dd,
.simple-note-detail-operation-row--sale dd {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.simple-note-detail-operation-row--customer dd .simple-note-detail-operation-link,
.simple-note-detail-operation-row--customer dd > span,
.simple-note-detail-operation-row--sale dd .simple-note-detail-operation-link,
.simple-note-detail-operation-row--sale dd > span {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.simple-note-detail-operation-link {
  color: #0070cd;
  text-decoration: none;
}

.simple-note-detail-operation-link:hover {
  color: #0070cd;
  text-decoration: none;
}

.simple-note-detail-operation-link--customer {
  font-weight: 700;
}

.simple-note-detail-operation-spacer,
.simple-note-detail-operation-meta-divider,
.simple-note-detail-operation-totals-divider {
  height: 1px;
  background: rgba(100, 116, 139, 0.34);
  margin: 8px 0;
}

.simple-note-detail-operation-meta-row {
  display: grid;
  grid-template-columns: minmax(102px, auto) minmax(0, 1fr);
  column-gap: 10px;
  align-items: start;
}

.simple-note-detail-operation-comment-text {
  margin: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.simple-note-detail-operation-comment-text--expanded {
  white-space: normal;
}

.simple-note-detail-operation-comment-toggle {
  border: 0;
  background: transparent;
  margin: 4px 0 0;
  padding: 0;
  color: #0070cd;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.simple-note-detail-operation-comment-toggle:hover {
  color: #005a9f;
}

.simple-note-detail-operation-totals-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 10px;
  align-items: baseline;
}

.simple-note-detail-operation-totals-row--total dt,
.simple-note-detail-operation-totals-row--total dd {
  font-weight: 800;
  letter-spacing: 0.01em;
}

.simple-note-detail-operation-totals-row--total dt {
  font-size: 14px;
}

.simple-note-detail-operation-totals-row--total dd {
  font-size: 13px;
  color: #0f172a;
}

.simple-note-detail-operation-row .app-status-pill {
  vertical-align: middle;
  min-height: 26px;
  font-size: 10px;
}

@media (max-width: 767px) {
  .simple-note-detail-operation-split {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .simple-note-detail-operation-pane--totals {
    border-left: 0;
    border-top: 1px solid rgba(148, 163, 184, 0.35);
    padding-left: 0;
    padding-top: 12px;
  }

  .simple-note-detail-operation-row {
    grid-template-columns: minmax(102px, auto) minmax(0, 1fr);
    column-gap: 10px;
    margin-bottom: 7px;
  }
}

.simple-note-status-pill--paid {
  color: #1c9b43;
  background: rgba(59, 212, 46, 0.14);
  border: 1px solid rgba(59, 212, 46, 0.3);
}

.simple-note-status-pill--partial {
  color: #1d4ed8;
  background: rgba(37, 99, 235, 0.12);
  border: 1px solid rgba(37, 99, 235, 0.28);
}

.simple-note-status-pill--pending {
  color: #b45309;
  background: #fff7e8;
  border: 1px solid rgba(251, 191, 36, 0.55);
}

.simple-note-status-pill--canceled {
  color: #b42318;
  background: #fff0f0;
  border: 1px solid rgba(197, 58, 58, 0.3);
}
</style>